/**
 * Build metadata for the Help page.
 *
 * Two consumers, one source: the build-info row renders {@link BuildInfo.formatted} and
 * the Copy button next to it writes that same field to the clipboard, so what a reporter
 * pastes into a GitHub issue cannot drift from what they read on screen. The structured
 * `environment` and `version` fields feed the issue-form prefill.
 *
 * Version, commit, and build date are compile-time constants injected by the `define`
 * block in `vite.config.ts` and declared in `vite-env.d.ts`. Every read goes through a
 * `typeof … === 'undefined'` guard, because those identifiers are absent both under vitest
 * (`vitest.config.ts` is standalone and applies no `define`) and in a build with no git
 * checkout. The environment label is not injected — see {@link ENVIRONMENT_BY_HOSTNAME}.
 */

/**
 * Deployed-environment labels. A named union rather than inline literals: these values
 * reach the issue-form `environment` prefill and the Door B receipt chip, so they are
 * domain values that triage reads, not display trivia.
 */
export type EnvironmentName = 'Production' | 'Development' | 'Sandbox' | 'Unknown';

/**
 * Stands in for any metadata field the build could not supply. Rendered verbatim, so an
 * absent commit reads as `build unavailable` — never `build undefined`.
 */
export const UNAVAILABLE = 'unavailable';

/** Separator from the prototype: U+00B7 MIDDLE DOT, one space either side. */
const SEPARATOR = ' · ';

const UNKNOWN_ENVIRONMENT: EnvironmentName = 'Unknown';

/**
 * Deployed web hostname → environment label. The three hostnames are the
 * `dns.webHostname` values in `infra/configuration/environments.ts`:
 *
 * - `timesheets.tech.utexas.edu` — prod (apex zone, account 003493199986)
 * - `dev.timesheets.tech.utexas.edu` — dev (delegated from the prod-owned zone)
 * - `timesheets-sandbox.eta.its.utexas.edu` — sandbox (delegated from the EIS `eta` zone)
 *
 * Anything else — `localhost`, a CloudFront default domain, a preview host, a fourth
 * environment nobody added here — falls through to `Unknown` rather than being
 * mislabelled as one of the three. A neutral label on a bug report is recoverable; a
 * confidently wrong one sends triage to the wrong account.
 *
 * This is inference, and it is the one piece of cleverness on the page. The honest
 * long-term source is CDK, which already knows the answer: `infra/bin/app.ts` selects the
 * `AccountConfig`, so the environment name could be threaded through `ServiceStackProps`
 * into the generated `/config.json` that `loadConfig()` in `config.ts` already fetches at
 * startup. That is deliberately deferred — it would widen this slice out of
 * `packages/web` for the sake of a label. Until it lands, a new environment means a new
 * row here, and forgetting to add one shows `Unknown` instead of a wrong answer.
 *
 * A `Map`, not an object literal: an object literal inherits `Object.prototype`, so a
 * hostname of `constructor` or `toString` would resolve to an inherited member instead of
 * falling through to `Unknown`, and that member would then be stringified into the
 * build-info row.
 */
const ENVIRONMENT_BY_HOSTNAME: ReadonlyMap<string, EnvironmentName> = new Map<
  string,
  EnvironmentName
>([
  ['timesheets.tech.utexas.edu', 'Production'],
  ['dev.timesheets.tech.utexas.edu', 'Development'],
  ['timesheets-sandbox.eta.its.utexas.edu', 'Sandbox'],
]);

export interface BuildInfo {
  /** Environment inferred from the hostname. Never blank — `Unknown` at worst. */
  readonly environment: EnvironmentName;
  /** Display version, `v`-prefixed (e.g. `v0.1.0`), or {@link UNAVAILABLE}. */
  readonly version: string;
  /** Short commit SHA (e.g. `a1b9c04`), or {@link UNAVAILABLE}. */
  readonly commit: string;
  /** UTC calendar date of the build as `YYYY-MM-DD`, or {@link UNAVAILABLE}. */
  readonly buildDate: string;
  /**
   * The single string the build-info row displays and the Copy button writes. Read this
   * field in both places; never re-derive it, or display and clipboard can diverge.
   */
  readonly formatted: string;
}

/** Everything {@link formatBuildInfo} needs — {@link BuildInfo} minus its own output. */
export type BuildInfoParts = Omit<BuildInfo, 'formatted'>;

/**
 * Builds the one displayed/copied string, e.g.
 * `Production · v1.4.2 · build a1b9c04 · 2026-08-23`.
 *
 * Every segment is a plain string, so a field that fell back to {@link UNAVAILABLE}
 * renders as `unavailable` in place — marked rather than silently dropped, so a triager
 * reading a pasted string can tell missing metadata from a reporter who trimmed it.
 */
export function formatBuildInfo(parts: BuildInfoParts): string {
  return [parts.environment, parts.version, `build ${parts.commit}`, parts.buildDate].join(
    SEPARATOR,
  );
}

/** Maps a hostname to its environment label, defaulting to `Unknown`. */
export function resolveEnvironmentName(hostname: string): EnvironmentName {
  // DNS hostnames are case-insensitive. Browsers already lower-case
  // `location.hostname`, but a caller-supplied value need not be.
  return ENVIRONMENT_BY_HOSTNAME.get(hostname.trim().toLowerCase()) ?? UNKNOWN_ENVIRONMENT;
}

function injectedVersion(): string {
  return typeof __APP_VERSION__ === 'undefined' ? UNAVAILABLE : `v${__APP_VERSION__}`;
}

function injectedCommit(): string {
  return typeof __APP_COMMIT__ === 'undefined' ? UNAVAILABLE : __APP_COMMIT__;
}

function injectedBuildDate(): string {
  return typeof __APP_BUILD_DATE__ === 'undefined' ? UNAVAILABLE : __APP_BUILD_DATE__;
}

/**
 * Assembles the build info for a hostname, defaulting to the current document's.
 *
 * @param hostname Overridable so tests can exercise each mapped host without navigating.
 */
export function resolveBuildInfo(
  hostname: string = typeof window === 'undefined' ? '' : window.location.hostname,
): BuildInfo {
  const parts: BuildInfoParts = {
    environment: resolveEnvironmentName(hostname),
    version: injectedVersion(),
    commit: injectedCommit(),
    buildDate: injectedBuildDate(),
  };
  return { ...parts, formatted: formatBuildInfo(parts) };
}

/**
 * Resolved once at module load: the injected constants are compile-time literals and the
 * hostname cannot change without a navigation, which reloads the bundle. Import this
 * rather than calling {@link resolveBuildInfo} again per render.
 */
export const buildInfo: BuildInfo = resolveBuildInfo();
